/**
 * Proiezione — stato DERIVATO dagli eventi (contratto §2.2/§2.3). Nessuna verità parallela:
 * l'UI, l'export e la metrica leggono TUTTI da qui. Logica pura = test obbligatorio (regola 8).
 *
 * Merge (§2.3): `set.updated` = merge-patch per-campo con i valori FINALI dei campi modificati;
 * applicati in ordine TOTALE D49 (`compareEvents`) sul base `set.created` → LWW per-campo:
 * l'ultimo evento in ordine totale vince per i campi che porta, i campi assenti restano invariati.
 * Corollario: un update "vecchio" che arriva dopo NON sovrascrive i campi già aggiornati da un
 * evento più recente (l'ordinamento lo mette prima e il fold lo applica prima).
 */
import { compareEvents, type HyprEvent } from "../storage/events";

export interface SetStato {
  set_id: string;
  session_id: string;
  exercise_id: string;
  set_number: number;
  reps: number;
  load_type: string;
  weight_kg: number;
  rir: number | null;
  is_warmup: boolean;
  notes: string;
  corretto: boolean; // ha ricevuto ≥1 set.updated (D52)
}

export interface SessioneStato {
  session_id: string;
  date: string;
  day_label: string;
  status: string; // §1.4: in_progress | completed | abandoned
  weight_kg: number | null;
  sleep_hours: number | null;
  energy_level: number | null;
  duration_min: number | null;
  notes: string;
}

function num(v: unknown): number | null {
  return typeof v === "number" && Number.isFinite(v) ? v : null;
}

function str(v: unknown): string {
  return typeof v === "string" ? v : "";
}

/**
 * Nome visualizzato corrente (D54): deriva da `user.created`/`user.updated` in ordine totale
 * D49 (LWW per-campo §2.3: l'ultimo `display_name` scritto vince). `""` = nessun account ancora.
 * Nessuna verità parallela: la UI e l'export leggono da qui.
 */
export function displayName(eventi: HyprEvent[]): string {
  const rilevanti = eventi
    .filter((e) => e.type === "user.created" || e.type === "user.updated")
    .sort(compareEvents);
  let nome = "";
  for (const e of rilevanti) {
    const v = e.data["display_name"];
    if (typeof v === "string") nome = v;
  }
  return nome;
}

/**
 * Stati dei set della sessione, in ordine di creazione (D49). `set.updated` senza base
 * (`set.created` assente — import parziali) viene ignorato: niente entità da patchare.
 * M2 porta il merge completo con property test; qui la semantica §2.3 è già quella.
 */
export function statiSet(eventi: HyprEvent[], session_id: string): SetStato[] {
  const basi = new Map<string, HyprEvent>();
  const aggiornamenti = new Map<string, HyprEvent[]>();
  for (const e of eventi) {
    if (e["session_id"] !== session_id) continue;
    const set_id = typeof e["set_id"] === "string" ? e["set_id"] : null;
    if (set_id === null) continue;
    if (e.type === "set.created") {
      if (!basi.has(set_id)) basi.set(set_id, e);
    } else if (e.type === "set.updated") {
      const lista = aggiornamenti.get(set_id) ?? [];
      lista.push(e);
      aggiornamenti.set(set_id, lista);
    }
  }
  const ordinati = [...basi.entries()].sort((a, b) => compareEvents(a[1], b[1]));
  const out: SetStato[] = [];
  for (const [set_id, base] of ordinati) {
    const d: Record<string, unknown> = { ...base.data };
    const updates = (aggiornamenti.get(set_id) ?? []).sort(compareEvents);
    for (const u of updates) {
      // merge-patch §2.3: campo presente vince col suo valore — `null` = RIMOZIONE del campo
      // opzionale (D53, RFC 7386 merge-patch): assegnare null fa tornare il campo a "assente"
      for (const [k, v] of Object.entries(u.data)) d[k] = v;
    }
    out.push({
      set_id,
      session_id,
      exercise_id: str(d["exercise_id"]),
      set_number: num(d["set_number"]) ?? 0,
      reps: num(d["reps"]) ?? 0,
      load_type: str(d["load_type"]) || "barbell",
      weight_kg: num(d["weight_kg"]) ?? 0,
      rir: num(d["rir"]),
      is_warmup: d["is_warmup"] === true,
      notes: str(d["notes"]),
      corretto: updates.length > 0,
    });
  }
  return out;
}

/** Stato della sessione = `session.created` + merge-patch di `session.updated`/`session.completed`. */
export function statoSessione(eventi: HyprEvent[], session_id: string): SessioneStato | null {
  let base: HyprEvent | null = null;
  const updates: HyprEvent[] = [];
  for (const e of eventi) {
    if (e["session_id"] !== session_id) continue;
    if (e.type === "session.created" && base === null) base = e;
    else if (e.type === "session.updated" || e.type === "session.completed") updates.push(e);
  }
  if (base === null) return null;
  const d: Record<string, unknown> = { ...base.data };
  for (const u of updates.sort(compareEvents)) {
    for (const [k, v] of Object.entries(u.data)) d[k] = v;
  }
  return {
    session_id,
    date: str(d["date"]),
    day_label: str(d["day_label"]),
    status: str(d["status"]) || "in_progress",
    weight_kg: num(d["weight_kg"]),
    sleep_hours: num(d["sleep_hours"]),
    energy_level: num(d["energy_level"]),
    duration_min: num(d["duration_min"]),
    notes: str(d["notes"]),
  };
}

/** Tutte le sessioni in ordine di creazione (D49). */
export function sessioni(eventi: HyprEvent[]): SessioneStato[] {
  const visti = new Set<string>();
  const out: SessioneStato[] = [];
  const ordinati = [...eventi].sort(compareEvents);
  for (const e of ordinati) {
    if (e.type !== "session.created") continue;
    const sid = typeof e["session_id"] === "string" ? e["session_id"] : null;
    if (sid === null || visti.has(sid)) continue;
    visti.add(sid);
    const s = statoSessione(eventi, sid);
    if (s) out.push(s);
  }
  return out;
}

/** Sessione aperta (`in_progress`): l'ULTIMA in ordine totale D49 se ce n'è più d'una. */
export function sessioneAttiva(eventi: HyprEvent[]): SessioneStato | null {
  const aperte = sessioni(eventi).filter((s) => s.status === "in_progress");
  return aperte.length > 0 ? (aperte[aperte.length - 1] as SessioneStato) : null;
}

/**
 * Ultimo set loggato dell'esercizio (qualunque sessione, esclusi i warmup), in ordine totale
 * D49. È la fonte della proposta di pre-fill (D20/D51): "proposta: ultimo set".
 */
export function ultimoSetEsercizio(eventi: HyprEvent[], exercise_id: string): SetStato | null {
  // gli eventi di creazione ordinati D49: l'ULTIMO è l'ultimo set dell'esercizio
  const creati = eventi
    .filter((e) => e.type === "set.created" && e.data["exercise_id"] === exercise_id && e.data["is_warmup"] !== true)
    .sort(compareEvents);
  const ultimoEvento = creati.length > 0 ? creati[creati.length - 1] : undefined;
  if (!ultimoEvento) return null;
  const sid = typeof ultimoEvento["session_id"] === "string" ? ultimoEvento["session_id"] : null;
  const set_id = typeof ultimoEvento["set_id"] === "string" ? ultimoEvento["set_id"] : null;
  if (sid === null || set_id === null) return null;
  return statoSetPerId(eventi, sid, set_id);
}

function statoSetPerId(eventi: HyprEvent[], session_id: string, set_id: string): SetStato | null {
  return statiSet(eventi, session_id).find((s) => s.set_id === set_id) ?? null;
}

/**
 * Campi davvero modificati tra base e correzione (§2.3: l'update porta SOLO i campi cambiati,
 * con il valore finale). Confronto per valore JSON: deterministico, nessuna sorpresa.
 */
export function campiModificati(base: Record<string, unknown>, modifica: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(modifica)) {
    if (JSON.stringify(base[k]) !== JSON.stringify(v)) out[k] = v;
  }
  return out;
}

/**
 * Metrica **D51** (decisione chiusa 2026-09-25): quota di set SALVATI identici alla proposta
 * attiva — cioè all'ultimo set dello stesso esercizio COM'ERA al momento del salvataggio
 * (è esattamente il valore pre-compilato da D20). DERIVATA dagli eventi: nessun campo contratto.
 *
 * Definizione precisa (le due versioni naive sono false):
 * - i valori del set = quelli del `set.created` (quello che l'utente ha SALVATO: una correzione
 *   successiva D52 non riscrive la storia — "set salvati identici", non "set finali identici");
 * - la proposta = lo stato dell'ultimo set precedente con solo gli `set.updated` DI PRIMA del
 *   salvataggio (un `set.updated` successivo non cambia cosa era proposto);
 * - solo set di lavoro (i warmup sono diversi per design); primo set dell'esercizio = non
 *   confrontabile.
 * Identico = stesso `load_type`, `reps` e `weight_kg` (il RIR non si pre-compila: lo sceglie
 * l'utente con un tap, non è oggetto di anchoring). Residuo dichiarato in D51: la metrica non
 * distingue "accettato senza leggere" da "voluto identico".
 */
export function metricaIdenticiUltimo(eventi: HyprEvent[], session_id: string): { identici: number; confrontabili: number } {
  const salvati = eventi
    .filter((e) => e.type === "set.created" && e["session_id"] === session_id && e.data["is_warmup"] !== true)
    .sort(compareEvents);
  let identici = 0;
  let confrontabili = 0;
  for (const s of salvati) {
    const proposta = propostaAlMomento(eventi, s);
    if (proposta === null) continue;
    confrontabili += 1;
    const reps = num(s.data["reps"]);
    const weight_kg = num(s.data["weight_kg"]) ?? 0;
    const load_type = str(s.data["load_type"]) || "barbell";
    if (reps === proposta.reps && weight_kg === proposta.weight_kg && load_type === proposta.load_type) identici += 1;
  }
  return { identici, confrontabili };
}

/** Stato della proposta (ultimo set dello stesso esercizio) COM'ERA prima del salvataggio di `s`. */
function propostaAlMomento(
  eventi: HyprEvent[],
  s: HyprEvent,
): { reps: number; weight_kg: number; load_type: string } | null {
  const precedenti = eventi
    .filter(
      (e) =>
        e.type === "set.created" &&
        e.data["exercise_id"] === s.data["exercise_id"] &&
        e.data["is_warmup"] !== true &&
        e["set_id"] !== s["set_id"] &&
        compareEvents(e, s) < 0,
    )
    .sort(compareEvents);
  const p = precedenti.length > 0 ? precedenti[precedenti.length - 1] : undefined;
  if (!p) return null;
  const set_id = p["set_id"];
  const d: Record<string, unknown> = { ...p.data };
  for (const u of eventi
    .filter((e) => e.type === "set.updated" && e["set_id"] === set_id && compareEvents(e, s) < 0)
    .sort(compareEvents)) {
    for (const [k, v] of Object.entries(u.data)) d[k] = v;
  }
  return {
    reps: num(d["reps"]) ?? 0,
    weight_kg: num(d["weight_kg"]) ?? 0,
    load_type: str(d["load_type"]) || "barbell",
  };
}

/**
 * Formato del rest timer (D19): `M:SS`, mai magie — 0 ms → "0:00", mai negativi.
 */
export function formatoResto(ms: number): string {
  const totale = Math.max(0, Math.ceil(ms / 1000));
  const m = Math.floor(totale / 60);
  const s = totale % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
}
